import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import PopupModal from "../../components/Modal/PopupModal";
import './DynamicFlow.scss';

const flowName = [
  { name: "Purchase Partner", iconType: "bi", icon: "bi-truck" },
  { name: "Customer Sales", iconType: "bi", icon: "bi-person" },
  { name: "Asset", iconType: "bi", icon: "bi-building" },
  { name: "Production", iconType: "bi", icon: "bi-gear-wide-connected" },
  { name: "AP Automation", iconType: "bi", icon: "bi-file-earmark-text" },
  { name: "Product Hub", iconType: "bi", icon: "bi-boxes" },
  { name: "Logistics", iconType: "bi", icon: "bi-truck-front" },
  { name: "Supply Inventory", iconType: "bi", icon: "bi-box" },
  { name: "Profit Ledger", iconType: "bi", icon: "bi-currency-rupee" },
];

const flowContent = [
  {
    img: "./assets/images/purchaseMng.png",
    content: {
      heading: "Purchase Partner",
      text: "Serves as a strategic framework to oversee and optimize relationships with external vendors, contractors, and suppliers. It focuses on aligning partner performance with corporate objectives to ensure high-quality delivery, cost-efficiency, and long-term scalability. By implementing standardized evaluation and communication protocols, organizations can effectively mitigate supply chain risks and foster collaborative innovation. Ultimately, this approach transforms traditional vendor interactions into value-driven partnerships that support sustainable business growth.",
    },
  },
  {
    img: "./assets/images/customerMng.png",
    content: {
      heading: "Customer Sales",
      text: "Focuses on the strategic orchestration of the entire sales lifecycle, from lead acquisition to final conversion and post-purchase engagement. It leverages data-driven insights to align sales strategies with customer needs, ensuring a seamless and personalized journey that maximizes lifetime value. By optimizing sales pipelines and fostering strong client relationships, organizations can drive sustainable revenue growth and improve market positioning. This systematic approach ensures that sales teams are empowered to deliver consistent results while maintaining high levels of customer satisfaction and loyalty.",
    },
  },
  {
    img: "./assets/images/AssetMng.png",
    content: {
      heading: "Asset Management",
      text: "Provides a centralized framework for supervising and maintaining an organization's high-value physical and digital resources throughout their entire lifecycle. It integrates strategic planning with real-time tracking to optimize performance, minimize operational downtime, and ensure rigorous regulatory compliance. By leveraging predictive maintenance and data-driven risk assessment, businesses can significantly extend asset longevity while reducing total cost of ownership. This proactive approach ensures that capital investments remain aligned with evolving enterprise goals and financial objectives.",
    },
  },
  {
    img: "./assets/images/factoryMng.png",
    content: {
      heading: "Production",
      text: "Orchestrates the complex integration of personnel, machinery, and materials to achieve peak manufacturing excellence and operational efficiency. It utilizes real-time data monitoring and Lean methodologies to streamline production workflows, minimize waste, and ensure stringent quality control standards are met. By synchronizing shop-floor activities with enterprise-level resource planning, the system enables agile responses to shifting market demands and supply chain fluctuations. Ultimately, this comprehensive oversight maximizes throughput while maintaining the highest safety and sustainability benchmarks across the production facility.",
    },
  },
  {
    img: "./assets/images/apAuto.png",
    content: {
      heading: "AP Automation",
      text: "(Accounts Payable Automation) refers to the strategic deployment of digital technologies to streamline the entire invoice-to-pay lifecycle. By utilizing AI-driven data extraction and automated three-way matching—aligning invoices with purchase orders and receiving reports—organizations can virtually eliminate manual data entry and human error. This transformation accelerates financial close cycles, strengthens internal controls against fraud, and enhances working capital management through timely payment scheduling. Ultimately, it shifts the finance department from a reactive cost center to a proactive, data-driven strategic partner.",
    },
  },
  {
    img: "./assets/images/ProductMng.png",
    content: {
      heading: "Product Hub and Life Cycle",
      text: "The integration of a Product Hub with Product Lifecycle Management (PLM) creates a unified digital thread that governs a product from its initial engineering concept to its final market delivery. While the PLM manages the strategic phases of R&D, compliance, and manufacturing revisions, the Product Hub acts as the centralized repository for the resulting commercial data and digital assets. This synergy ensures that every stakeholder—from design engineers to marketing teams—operates from a Single Source of Truth accelerating time-to-market and ensuring that complex product specifications remain consistent across all global distribution channels.",
    },
  },
  {
    img: "./assets/images/LogisticsMng.png",
    content: {
      heading: "Logistics Management",
      text: "Logistics Management is the strategic process of planning, implementing, and controlling the efficient movement and storage of goods, services, and information from the point of origin to the point of consumption. It integrates complex functions such as transportation, warehousing, materials handling, and order fulfillment into a cohesive operation to meet customer requirements with precision. By leveraging real-time tracking and predictive analytics, organizations can optimize route efficiency, reduce lead times, and significantly lower operational overhead. This comprehensive oversight ensures a resilient supply chain that can rapidly adapt to global market shifts while maintaining high service-level standards.",
    },
  },
  {
    img: "./assets/images/supplyMNg.png",
    content: {
      heading: "Supply Inventory",
      text: "Supply Inventory Management is a critical operational discipline focused on the systematic oversight of stock levels to ensure a seamless balance between supply and demand. It utilizes advanced forecasting models and real-time tracking to minimize carrying costs while preventing stockouts that could disrupt production or fulfillment cycles. By integrating automated replenishment triggers and rigorous audit protocols, organizations gain granular visibility into their material flow and working capital. This strategic approach enhances supply chain resilience, allowing businesses to maintain lean operations while remaining agile enough to respond to sudden market fluctuations.",
    },
  },
  {
    img: "./assets/images/ProfitMng.png",
    content: {
      heading: "Profit Ledger",
      text: "Profit Ledger is a comprehensive financial management tool designed to provide real-time visibility into organizational profitability across multiple dimensions. By integrating cost accounting, revenue tracking, and margin analysis, it enables executives to make data-driven decisions that optimize financial performance. This strategic approach enhances supply chain resilience, allowing businesses to maintain lean operations while remaining agile enough to respond to sudden market fluctuations.",
    },
  },
];

const DynamicFlow = () => {
  const [selectedFlow, setSelectedFlow] = useState(0);
  const [getPopUpShow, setPopUpShow] = useState(false);
  const flowRefs = useRef([]);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-play
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSelectedFlow((prev) => (prev + 1) % flowName.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Auto-scroll to center
  useEffect(() => {
    const activeElement = flowRefs.current[selectedFlow];
    const container = containerRef.current;
    
    if (activeElement && container) {
      const elementRect = activeElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;
      const elementCenter = elementRect.left + elementRect.width / 2 - containerRect.left + scrollLeft;
      const containerCenter = containerRect.width / 2;
      const targetScroll = elementCenter - containerCenter;
      
      container.scrollTo({ left: targetScroll, behavior: "smooth" });
    }
  }, [selectedFlow]);

  const handleFlowClick = (index) => {
    setSelectedFlow(index);
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setSelectedFlow((prev) => (prev + 1) % flowName.length);
      }, 5000);
    }, 10000);
  };

  return (
    <Container className="dynamicflow py-4">
      <PopupModal show={getPopUpShow} onHide={() => setPopUpShow(false)} />
      
      <Row className="g-3 align-items-stretch">
        <div className="why-headers">  <h1>Explore our best-of-breed solutions</h1></div>
      
        {/* Image */}
        <Col xs={12} md={7}>
          <div className="img-container">
            <img
              className="img-fluid"
              src={flowContent[selectedFlow]?.img}
              alt={flowContent[selectedFlow]?.content?.heading}
            />
          </div>
        </Col>

        {/* Content */}
        <Col xs={12} md={5}>
          <div className="flowcontent">
            <div>
              <h5 className="fw-bold">{flowContent[selectedFlow]?.content?.heading}</h5>
              <p className="text-justify">
                {flowContent[selectedFlow]?.content?.text}
              </p>
            </div>
            <button className="main-btn" onClick={() => setPopUpShow(true)}>
              Book a Demo
            </button>
          </div>
        </Col>

        {/* Icons */}
        <Col xs={12} className="mt-3">
          <div className="flow-container">
            <div className="flow-wrapper" ref={containerRef}>
              {flowName.map((data, i) => (
                <div
                  key={i}
                  ref={(el) => (flowRefs.current[i] = el)}
                  onClick={() => handleFlowClick(i)}
                  className={`flow-tile ${i === selectedFlow ? "active" : ""}`}
                >
                  <div className="icon-box">
                    {data.iconType === "bi" ? (
                      <i className={`bi ${data.icon}`} />
                    ) : (
                      <img src={data.icon} alt={data.name} />
                    )}
                  </div>
                  <p>{data.name}</p>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default DynamicFlow;
